import type { Session, SessionStatus } from "@mux/sdk/v2/client"
import { batch, createContext, createEffect, createMemo, createSignal, getOwner, onCleanup, onMount, type ParentProps, untrack, useContext } from "solid-js"
import { createStore, reconcile, unwrap } from "solid-js/store"
import { useLanguage } from "@/context/language"
import { getActiveGridSessionIDs } from "@/pages/grid/grid-active-sessions"
import { RevisionedCache, persisted } from "@/storage/persist"
import { activeSessionTargets, sanitizeProject } from "./global-sync/utils"
import { useGlobalSDK } from "./global-sdk"
import { useServer } from "./server"
import { invalidateProviders, useProviders } from "@/hooks/use-providers"
import { invalidateAccountPreferences } from "@/context/account-preference"
import { createGlobalBootstrapController } from "./global-sync/bootstrap-controller"
import { loadIfStreamSilent } from "./first-snapshot"
import { hydrateCachesWhileStarting, hydrateGlobalStore } from "./global-sync/cache-hydration"
import { createChildStoreManager } from "./global-sync/child-store"
import { createGlobalEventController } from "./global-sync/event-controller"
import { createInitialGlobalStore } from "./global-sync/global-store"
import { createProjectController, createProjectIndex } from "./global-sync/project-store"
import { createRefreshQueue } from "./global-sync/queue"
import { createRequestSnapshotController } from "./global-sync/request-snapshot"
import { createOpenRequests } from "./global-sync/request-index"
import { createQuestionResponseController } from "./global-sync/question-response"
import { respondToQuestion } from "@/transport/questions"
import { getOpenRequests } from "@/transport/requests"
import { getSessionWaiting, getSessionsByID } from "@/transport/session"
import { isAbortError } from "@/utils/server-errors"
import { createForkActivationController } from "./session/fork-activation"
import { listStarterMessages, refreshStarterMessages, starterMessagesServerURL, useStarterMessagesVersion } from "./session/starters"
import { createStarterPageRepository } from "@/storage/starter-pages"
import { createSessionSync } from "./global-sync/session-sync"
import { createTranscriptWatchController } from "./global-sync/transcript-watch-controller"
import { cachedSidebarRunState, sidebarProjectionCache, staleCachedOracleIDs } from "./global-sync/sidebar-projection"
import { createSidebarController } from "./global-sync/sidebar-controller"
import { createSessionStatusController } from "./global-sync/status-controller"
import { createSessionWaitingController } from "./global-sync/waiting-controller"
import type { GlobalStore } from "./global-sync/types"
import { createGlobalStoreOperations } from "./global-sync/store-operations"
import type { GlobalBootstrapCache, SidebarProjectionCache } from "./global-sync/types"

function createGlobalSync() {
  const globalSDK = useGlobalSDK()
  const language = useLanguage()
  const server = useServer()
  const owner = getOwner()
  if (!owner) throw new Error("GlobalSync must be created within owner")

  const [bootstrapCache, setBootstrapCache, bootstrapInit] = persisted(
    RevisionedCache.global(`globalSync.bootstrap:${server.key || "no-server"}`),
    createStore<{ value?: GlobalBootstrapCache }>({ value: undefined }),
  )
  const [sidebarCache, setSidebarCache, sidebarInit] = persisted(
    // Rewritten with each run-state change; a burst of changes is written once.
    { ...RevisionedCache.global(`globalSync.sidebarProjection.v2:${server.key || "no-server"}`), writeDelayMs: 500 },
    createStore<{ value?: SidebarProjectionCache }>({ value: undefined }),
  )
  let bootstrapWritten = false
  let sidebarWritten = false
  const [data, writeStore] = createStore(createInitialGlobalStore({ bootstrap: bootstrapCache.value }))
  const setStore = ((...args: unknown[]) => {
    const key = args[0]
    if (typeof key !== "string" || key === "path" || key === "project" || key === "config" || key === "machineWorkspace") bootstrapWritten = true
    if (typeof key !== "string" || key === "session" || key === "session_status" || key === "session_waiting" ||
      key === "question" || key === "permission" || key === "request_route") sidebarWritten = true
    Reflect.apply(writeStore, undefined, args)
  }) as typeof writeStore
  const providers = useProviders()
  createEffect(() => {
    setStore("provider", reconcile(providers.inventory()))
  })
  const cachesSynchronous = !(bootstrapInit instanceof Promise) && !(sidebarInit instanceof Promise)
  const [cacheReady, setCacheReady] = createSignal(cachesSynchronous)
  const workspacesKnown = () => cacheReady() && data.project.length > 0
  let active = true
  let cancelFirstProjection: (() => void) | undefined
  // The cache's records, until the machine's list of current Oracles shows
  // which of its Oracles were replaced while the page was closed.
  let cachedSessions: Session[] = []
  const applySidebarCache = () => {
    if (!active || sidebarWritten || !sidebarCache.value) return
    const { statuses, waiting, requests } = cachedSidebarRunState(sidebarCache.value)
    cachedSessions = sidebarCache.value.sessions
    writeStore("session", reconcile(sidebarCache.value.sessions, { key: "id" }))
    writeStore("session_status", reconcile(statuses))
    writeStore("session_waiting", reconcile(waiting))
    writeStore("question", reconcile(requests.question))
    writeStore("permission", reconcile(requests.permission))
    writeStore("request_route", reconcile(requests.request_route))
  }
  applySidebarCache()
  const operations = createGlobalStoreOperations(setStore)
  const requestSnapshots = createRequestSnapshotController({ store: data, setStore })

  const cacheBootstrap = () => {
    bootstrapWritten = true
    setBootstrapCache("value", {
      path: structuredClone(unwrap(data.path)),
      project: data.project.map(sanitizeProject),
      provider: structuredClone(unwrap(data.provider)),
      config: structuredClone(unwrap(data.config)),
      machineWorkspace: structuredClone(unwrap(data.machineWorkspace)),
      updatedAt: Date.now(),
    })
  }
  const applyHydratedCache = () => {
    if (!active || bootstrapWritten || !bootstrapCache.value) return
    hydrateGlobalStore(writeStore, bootstrapCache.value)
  }

  const statuses = createSessionStatusController({
    setStore,
    load: () => globalSDK.client.session.status().then((result) => result.data ?? {}),
    connected: () => !!server.current,
  })
  const waiting = createSessionWaitingController({
    setStore,
    load: () => {
      const connection = server.current?.http
      return connection ? getSessionWaiting(connection) : Promise.reject(new Error("No active server"))
    },
    connected: () => !!server.current,
  })
  // Once the machine's snapshots have replaced every part the cache drew, the
  // cache follows the store, so a reload draws what this page last showed.
  // Until then the store still holds some of the cache's own copy, which must
  // not be stamped as current.
  createEffect(() => {
    if (!cacheReady() || !statuses.confirmed || !waiting.confirmed || !requestSnapshots.confirmed) return
    setSidebarCache("value", sidebarProjectionCache(data, Date.now()))
  })
  // Waiting counts reload wherever run statuses or the sidebar projection do,
  // so the same gaps are covered; a failure leaves the statuses unaffected.
  const refreshWaiting = () => void waiting.refresh().catch((error) => {
    console.warn("Failed to load chats waiting on launches", error)
  })
  const refreshRunState = () => {
    refreshWaiting()
    return statuses.refresh()
  }
  const clientFor = (directory: string) => globalSDK.createClient({ directory, throwOnError: true })
  const loadOpenRequests = (signal?: AbortSignal) => {
    const connection = server.current?.http
    return connection ? getOpenRequests(connection, signal) : Promise.reject(new Error("No active server"))
  }
  const question = createQuestionResponseController({
    snapshots: requestSnapshots,
    send: (directory, requestID, answers, signal) => respondToQuestion(
      globalSDK.server, directory, requestID, answers, { signal, fetch: globalSDK.transportFetch },
    ),
    load: loadOpenRequests,
  })
  let transcriptWatch: ReturnType<typeof createTranscriptWatchController> | undefined
  const session = createSessionSync({
    data,
    set: setStore,
    clientFor,
    cacheServerKey: () => server.key,
    live: globalSDK.event.watching,
    streamCursor: globalSDK.event.cursor,
    onSessionSeed: (directory, info, options) => {
      if (options?.isNew && !info.parentID && !info.hidden && !info.time?.archived) {
        operations.sessions.total(directory, (value) => (value ?? 0) + 1)
      }
    },
    onTrackedChange: () => transcriptWatch?.changed(),
  })
  const recoveringSessions = new Set<string>()
  const [recoveryCount, setRecoveryCount] = createSignal(0)
  const recoverSession = (directory: string, sessionID: string) => {
    if (recoveringSessions.has(sessionID)) return
    recoveringSessions.add(sessionID)
    setRecoveryCount(recoveringSessions.size)
    void session.sync(directory, sessionID, { force: true }).catch((error) => {
      console.warn("Failed to recover session transcript", { sessionID, error })
    }).finally(() => {
      recoveringSessions.delete(sessionID)
      setRecoveryCount(recoveringSessions.size)
    })
  }
  const activeSessions = () => [
    ...(typeof location === "undefined"
      ? []
      : activeSessionTargets(location.pathname, location.search, data.session, getActiveGridSessionIDs())),
    // Mounted surfaces include hidden sessions no route or Grid ID names (a Grid
    // Oracle window, a comparison branch); they must not stay behind a gap.
    ...session.retainedSessions(),
  ]
  transcriptWatch = createTranscriptWatchController({
    tracked: session.trackedSessions,
    covered: globalSDK.event.covered,
    since: session.watchSince,
    watch: globalSDK.event.setWatch,
    onGaps: globalSDK.event.onWatchGaps,
    markStale: session.markStale,
    currentSince: session.currentSince,
    active: activeSessions,
    recover: recoverSession,
  })
  const reconcileTranscriptResets = () => {
    const connection = server.current?.http
    if (!connection) return
    void session.reconcileTranscriptResets((sessionIDs, signal) => getSessionsByID(connection, sessionIDs, signal)).catch((error) => {
      if (!isAbortError(error)) console.warn("Failed to reconcile cached transcripts with server resets", error)
    })
  }

  let bootstrap: ReturnType<typeof createGlobalBootstrapController>
  let sidebar: ReturnType<typeof createSidebarController>
  const queue = createRefreshQueue({
    bootstrap: () => bootstrap.refreshGlobal(),
    bootstrapInstance: (directory) => bootstrap.refreshDirectory(directory),
  })
  const children = createChildStoreManager({
    owner,
    isBooting: (directory) => bootstrap?.isPending(directory) ?? false,
    isLoadingSessions: (directory) => sidebar?.isLoading(directory) ?? false,
    onBootstrap: (directory) => void bootstrap.ensureDirectory(directory),
    onDispose: (directory) => {
      bootstrap?.forgetDirectory(directory)
      sidebar?.disposeDirectory(directory)
      queue.clear(directory)
      session.clearDirectory(directory)
    },
    translate: language.t,
  })
  sidebar = createSidebarController({
    owner,
    client: globalSDK.client,
    serverKey: () => server.key,
    connection: () => server.current?.http,
    store: data,
    setStore,
    retained: session.retained,
    pin: children.pin,
    unpin: children.unpin,
    child: (directory) => children.child(directory, { bootstrap: false }),
    overlayStatuses: statuses.overlay,
    refreshProjectionWith: statuses.refreshWith,
  })
  bootstrap = createGlobalBootstrapController({
    globalClient: globalSDK.client,
    connection: () => server.current?.http,
    createClient: clientFor,
    hasServer: () => !!server.current,
    store: data,
    setStore,
    ensureChild: children.ensureChild,
    vcsCache: (directory) => children.vcsCache.get(directory),
    pin: children.pin,
    unpin: children.unpin,
    loadSessions: sidebar.load,
    seedOracles: (oracles) => batch(() => {
      for (const oracle of oracles) if (oracle.workspaceID) session.seed(oracle.workspaceID, oracle)
      const stale = staleCachedOracleIDs(cachedSessions, oracles, data.session)
      cachedSessions = []
      if (stale.size > 0) setStore("session", (sessions) => sessions.filter((item) => !stale.has(item.id)))
    }),
    refreshStatuses: refreshRunState,
    cacheGlobal: cacheBootstrap,
    requestFailedTitle: language.t("common.requestFailed"),
    translate: language.t,
    formatMoreCount: (count) => language.t("common.moreCountSuffix", { count }),
    refreshRequests: () => requestSnapshots.refresh(loadOpenRequests),
  })

  const projectsMap = createMemo(() => createProjectIndex(data.project))
  const starterPages = createStarterPageRepository()
  const forks = createForkActivationController({
    clientFor,
    getSession: session.get,
    workspaceDirectory: (workspaceID) => projectsMap().get(workspaceID)?.directory,
    seed: session.seed,
    messages: (sessionID) => data.message[sessionID],
    parts: (messageID) => data.part[messageID],
    installProvisional: session.installProvisionalTranscript,
    install: session.installActivatedSession,
    discard: session.discard,
    pages: starterPages,
    pageScope: server.key,
  })
  // Starters belong to the app's current machine; only that machine's scope keeps their pages.
  const starterVersion = useStarterMessagesVersion()
  createEffect(() => {
    starterVersion()
    const url = server.current?.http.url
    if (!url || starterMessagesServerURL() !== url) return
    untrack(() => forks.setStarters(listStarterMessages()))
  })
  const requests = createOpenRequests(data)
  globalSDK.event.registerMapper?.((directory) => {
    if (directory === "global") return "global"
    return projectsMap().get(directory)?.id ?? directory
  })
  const events = createGlobalEventController({
    globalStore: data,
    setGlobalStore: setStore,
    setProjects: (next) => typeof next === "function" ? operations.projects.mutate(next) : operations.projects.replace(next),
    refreshGlobal: queue.refresh,
    ensureGlobal: () => void bootstrap.ensureGlobal(),
    pushDirectory: queue.push,
    directories: () => Object.keys(children.children),
    resyncDirectories: globalSDK.event.covered,
    getDirectoryStore: (directory) => children.children[directory],
    markDirectory: children.mark,
    getVcsCache: (directory) => children.vcsCache.get(directory),
    loadLsp: (directory, apply, signal) => void clientFor(directory).lsp.status(undefined, { signal, throwOnError: true }).then((result) => apply(result.data ?? [])).catch((error) => {
      if (!signal?.aborted) console.warn("Failed to refresh workspace language-server status", { directory, error })
    }),
    refreshSessionStatuses: refreshRunState,
    refreshSidebar: () => {
      refreshWaiting()
      return sidebar.refresh()
    },
    reconcileTranscriptResets,
    recoverSession: session.recoverText,
    acceptSessionStatus: statuses.accept,
    recordSessionStatus: statuses.changed,
    acceptSessionWaiting: waiting.accept,
    invalidateSession: session.invalidate,
    markSessionMissing: session.markMissing,
    serverKey: () => server.key,
    preservePendingModelSelection: session.preservePendingModelSelection,
    acceptRequestEvent: requestSnapshots.record,
    onSettingsChanged: () => {
      invalidateAccountPreferences()
      void invalidateProviders().catch(() => undefined)
    },
    onStartersChanged: () => {
      void refreshStarterMessages().catch(() => undefined)
    },
    onSessionEvent: (directory, event) => {
      void session.persistEvent(directory, event)?.catch(() => {
        console.warn("Transcript reset recovery failed; reconnect to retry")
      })
    },
    listen: globalSDK.event.listen,
    start: globalSDK.event.start,
    connected: () => !!server.current,
  })

  const project = createProjectController({
    setProjects: (next) => typeof next === "function" ? operations.projects.mutate(next) : operations.projects.replace(next),
    setMeta: children.projectMeta,
    setIcon: children.projectIcon,
  })

  onMount(() => {
    void hydrateCachesWhileStarting({
      initializers: [bootstrapInit, sidebarInit],
      active: () => active,
      hydrate() {
        batch(() => {
          applyHydratedCache()
          applySidebarCache()
          setCacheReady(true)
        })
      },
      start() {
        if (!server.current) {
          setStore("ready", true)
          return
        }
        void bootstrap.ensureGlobal()
        // The stream's first server.connected loads the sidebar projection (see
        // the event handler), so no change falls between snapshot and stream.
        cancelFirstProjection = loadIfStreamSilent(globalSDK.event.listen, () => {
          refreshWaiting()
          void sidebar.refreshProjection().catch(() => undefined)
        })
        events.start()
      },
    })
  })
  onCleanup(() => {
    active = false
    cancelFirstProjection?.()
    question.dispose()
    events.dispose()
    queue.dispose()
    bootstrap.dispose()
    sidebar.dispose()
    statuses.dispose()
    forks.dispose()
    starterPages.dispose()
    session.dispose()
    for (const directory of Object.keys(children.children)) children.disposeDirectory(directory, true)
  })

  return {
    data,
    projectsMap,
    /** Open questions and permission asks, by the chats they surface in. */
    requests,
    get sidebarReady() { return data.ready || workspacesKnown() },
    /** The machine's workspaces are on hand, from its snapshot or this device's cache of one. */
    get workspacesKnown() { return workspacesKnown() },
    get ready() { return data.ready },
    /** A global snapshot came from the machine; `ready` is also set when it could not be reached. */
    get loaded() { return bootstrap.loaded() },
    get sessionStatusReady() { return statuses.ready },
    catchingUp: () => globalSDK.event.catchingUp() || recoveryCount() > 0,
    refreshSessionStatuses: refreshRunState,
    get error() { return data.error },
    child: children.child,
    sidebar: sidebar.store,
    peekChild: (directory: string) => children.children[directory],
    disposeDirectory: children.disposeDirectory,
    suspendDirectory: bootstrap.suspendDirectory,
    isLoadingSessions: sidebar.isLoading,
    bootstrap: bootstrap.refreshGlobal,
    refreshSidebar: sidebar.refresh,
    bootstrapDirectory: bootstrap.ensureDirectory,
    project,
    question,
    session,
    forks,
    mutations: operations,
    status: {
      set(sessionID: string, value: SessionStatus) {
        statuses.settle(sessionID, value)
        statuses.changed(sessionID, value)
        operations.sessions.status(sessionID, value)
      },
      abort: statuses.beginAbort,
      aborting: statuses.aborting,
    },
    followup: {
      set(sessionID: string, items: GlobalStore["session_followup"][string]) {
        setStore("session_followup", sessionID, items)
      },
    },
  }
}

const GlobalSyncContext = createContext<ReturnType<typeof createGlobalSync>>()

export function GlobalSyncProvider(props: ParentProps) {
  const value = createGlobalSync()
  return <GlobalSyncContext.Provider value={value}>{props.children}</GlobalSyncContext.Provider>
}

export function useGlobalSync() {
  const context = useContext(GlobalSyncContext)
  if (!context) throw new Error("useGlobalSync must be used within GlobalSyncProvider")
  return context
}
